import { VENUE_PHOTO_MAX_PIXELS } from '@bonsai/shared';

import type { PhotoSize } from './photo-crop';

const BYTES_PER_MB = 1024 * 1024;

/** Photos bigger than this are redrawn smaller in the browser before they are framed. */
export const PHOTO_SHRINK_ABOVE_BYTES = 8 * BYTES_PER_MB;
/** Photos with a longer side than this (pixels) are redrawn so the longer side is this long. */
export const PHOTO_TARGET_LONG_EDGE_PX = 4000;
/** Files bigger than this are not even opened, to keep the browser's memory safe. */
export const PHOTO_HARD_CAP_BYTES = 50 * BYTES_PER_MB;
/** The most pixels iOS Safari lets a canvas hold. */
export const MAX_CANVAS_PIXELS = 16_700_000;
export const PHOTO_HARD_CAP_MESSAGE = `That photo is too large to use. Choose one under ${PHOTO_HARD_CAP_BYTES / BYTES_PER_MB} MB.`;

/** Whether a decoded photo should be redrawn smaller. Normal photos are used exactly as they are. */
export function needsShrink(bytes: number, photo: PhotoSize): boolean {
  return (
    bytes > PHOTO_SHRINK_ABOVE_BYTES ||
    photo.width * photo.height > VENUE_PHOTO_MAX_PIXELS ||
    Math.max(photo.width, photo.height) > PHOTO_TARGET_LONG_EDGE_PX
  );
}

/**
 * The size to redraw a photo at: the longer side brought down to the target, same shape, never
 * larger than the original, and never more pixels than a canvas can hold.
 */
export function shrunkSize(photo: PhotoSize): PhotoSize {
  const longEdge = Math.max(photo.width, photo.height);
  let scale = Math.min(1, PHOTO_TARGET_LONG_EDGE_PX / longEdge);
  const areaScale = Math.sqrt(MAX_CANVAS_PIXELS / (photo.width * photo.height * scale * scale));
  if (areaScale < 1) scale *= areaScale;
  return {
    width: Math.max(1, Math.floor(photo.width * scale)),
    height: Math.max(1, Math.floor(photo.height * scale)),
  };
}
